import React, {useMemo, useState} from 'react';
import {
	AssignedRequirement,
	Requirement,
	VehicleProject,
	VehicleProjectMaster,
} from 'types';
import {funcDecodeHex24ToDocId} from 'components/InfinityList';
import {useNavigate, useParams} from 'react-router-dom';
import {useGetVehicleProjectRequirementsSortOrderLazyQuery} from '../hooks/AssigedRequirementsInfinity.generated';
import {GetVehicleProjectDetailsQuery} from '../hooks';
import {BaselineDetailsPageParams} from '../../Baselines/BaselineDetailsPage/BaselineDetailsPage.types';
import {mapProjectRequirement} from './AssignedRequirementColumns';
import {AssignedRequirementsList} from './AssignedRequirementsList';
import {
	GetBaselineByIdQuery,
	useGetBaselineRequirementsInfinityLazyQuery,
	useGetBaselineRequirementsNextLazyQuery,
} from 'features/Baselines/hooks/baseline.generated';

type RequirementTabsProps = {
	data: GetBaselineByIdQuery | undefined;
	dataVehPro: GetVehicleProjectDetailsQuery | undefined;
	hidden: boolean;
};

type QueryDocumentsDBArg = {
	queryReq: string;
	queryAssignedReq: string;
};

const BaselineDetailsTabRequirements: React.FC<RequirementTabsProps> = ({
	data,
	dataVehPro,
	hidden,
}) => {
	const {modelSeries, generation, id} =
		useParams() as unknown as BaselineDetailsPageParams;

	const [getSortOrder] = useGetVehicleProjectRequirementsSortOrderLazyQuery();

	const [
		getAssignedRequirementsQuery,
		{loading: loadingQuery, data: dataQuery},
	] = useGetBaselineRequirementsInfinityLazyQuery();

	const [getRequirementsNext, {loading: loadingDocuments}] =
		useGetBaselineRequirementsNextLazyQuery();

	const arrIds = useMemo(
		() => dataQuery?.baselineRequirementsQuery?.requirementIds ?? [],
		[dataQuery],
	);

	const dictVehicleProjects: Record<string, VehicleProject> = useMemo(
		() =>
			(dataVehPro?.vehicleProjectsByModelSeriesAndGeneration ?? []).reduce(
				(acc, item) => {
					acc[funcDecodeHex24ToDocId(item.id)] = item as VehicleProject;
					return acc;
				},
				{} as Record<string, VehicleProject>,
			),
		[dataVehPro],
	);

	const queryDocumentsDB = async (
		query: QueryDocumentsDBArg,
		sortField: string,
		sortOrder: string,
	) => {
		const variables = {
			queryArgsBaselineRequirments: {
				baselineId: funcDecodeHex24ToDocId(id),
				queryAssignedReq: query.queryAssignedReq,
				queryArgs: {query: query.queryReq, sortField, sortOrder},
			},
		};

		const {data: dataQuery} = await getAssignedRequirementsQuery({
			variables,
		});

		const arrDocumentNext = (
			dataQuery?.baselineRequirementsQuery?.assignedRequirementsBatch ?? []
		).map(x => mapProjectRequirement(x as AssignedRequirement));

		const arrIDsNew =
			dataQuery?.baselineRequirementsQuery?.requirementIds ?? [];
		return {arrDocumentNext, arrIDsNew};
	};
	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const {data: dataDocumentsNext} = await getRequirementsNext({
			variables: {
				nextArgsBaselineRequirments: {
					baselineId: funcDecodeHex24ToDocId(id),
					nextArgs: {preFilteredIds},
				},
			},
		});

		return (dataDocumentsNext?.baselineRequirementsNext ?? []).map(x =>
			mapProjectRequirement(x as AssignedRequirement),
		);
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const {data: dataSortOrder} = await getSortOrder({
			variables: {
				sortArgs: {sortField, preFilteredIds},
			},
		});

		return dataSortOrder?.requirementsSortOrder ?? [];
	};

	const requirementFilterOptions =
		dataQuery?.baselineRequirementsQuery?.requirementFilterOptions;

	const assignedRequirementFilterOptions =
		dataQuery?.baselineRequirementsQuery?.assignedRequirementFilterOptions;

	const baselineName = data?.baselineById?.name;

	const assignedRequirementsListprops = {
		baselineName,
		loadingQuery,
		loadingDocuments,
		hidden,
		arrIds,
		requirementFilterOptions,
		assignedRequirementFilterOptions,
		dictVehicleProjects,
		vehicleProjectMaster:
			dataVehPro?.vehicleProjectMasterByModelSeriesAndGeneration as VehicleProjectMaster,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
	};

	return <AssignedRequirementsList {...assignedRequirementsListprops} />;
};
export default BaselineDetailsTabRequirements;
